import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useWeekViewContext } from "@/contexts/WeekView";
import { SystemBanner } from "@/ui/molecules/SystemBanner";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { StaffingViewLoading } from "../StaffingViewLoading";

/**
 * A staffing view while its week loads from the schedule cache: a spinner, or the error with Retry when it failed,
 * and the view once it's in. `waitsForStaffList` waits for the roster too, for a view that reads whole nurses; the
 * Week grid shows from the schedule's own people. With the cache off the week is the old list, which is already loaded.
 */
export const WeekScheduleLoadState: FC<{ waitsForStaffList: boolean; children: ReactNode }> = ({ waitsForStaffList, children }) => {
	const { t } = useTranslation();
	const weekView = useWeekViewContext();
	const isLoading = waitsForStaffList ? weekView.isLoading : weekView.isScheduleLoading;
	const isError = waitsForStaffList ? weekView.isError : weekView.isScheduleError;

	if (isError) {
		return (
			<SystemBanner
				tone="error"
				title={t("staffing.week.load.failed")}
				action={
					<SystemButton buttonVariant="secondary" size="small" onClick={weekView.refetch} data-testid="week-schedule-retry">
						{t("retry")}
					</SystemButton>
				}
				sx={{ mx: 2, mt: 1 }}
				dataTestId="week-schedule-load-error"
			/>
		);
	}
	if (isLoading) return <StaffingViewLoading dataTestId="week-schedule-loading" />;
	return children;
};
